import { DateTime } from "luxon";

import { EnumMyleLoginPermission } from "@/Common/Models/EnumMyleLoginPermission";
import { Guid } from "@/Common/Models/Guid";
import { SitesModel } from "@/Common/Models/Sites/SitesModel";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { BillingClaimInvoiceStatus } from "../BillingClaimInvoiceStatus";
import { BillingClaimStatusFilterSettings } from "../Settings/BillingClaimStatusFilterSettings";

class BillingClaimStatusInboxModel {
    public orderedSites: SitesModel[];
    public allClinicians: EmployeeUIModel[];
    public dateFrom: Date;
    public dateTo: Date;
    public selectedSiteIds: Guid[] = [];
    public selectedProviderIds: Guid[] = [];
    public selectedInvoiceStatus: number[] = [];
    public queryPageSize: number = 50;
    public isIgnored: boolean;
    public providerSearchText: string = "";
    public followCurrentTime: boolean = true;

    constructor() {
        this.orderedSites = cacheStore.SiteCache.sortByProperty(sites => sites.Model.ClinicalName.toLocaleLowerCase());
        if (!sessionStore.hasPermission(EnumMyleLoginPermission.BillingTabPermission))
            this.allClinicians = cacheStore.EmployeeCache.filter(e => e.ContactFacet_id === sessionStore.getCurrentEmployee().ContactFacetId)
                .map(p => EmployeeUIModel.fromCompositeObjectByContactFacetId(p));
        else
            this.allClinicians = cacheStore.EmployeeCache.map(p => EmployeeUIModel.fromCompositeObjectByContactFacetId(p));
    }

    public static toCacheModel(billingClaimStatusSettings: BillingClaimStatusInboxModel): BillingClaimStatusFilterSettings {
        return {
            DateFrom: billingClaimStatusSettings.dateFrom,
            DateTo: billingClaimStatusSettings.followCurrentTime ? new Date() : billingClaimStatusSettings.dateTo,
            QueryPageSize: billingClaimStatusSettings.queryPageSize,
            SelectedSiteIds: [...billingClaimStatusSettings.selectedSiteIds],
            SelectedProviderIds: [...billingClaimStatusSettings.selectedProviderIds],
            SelectedInvoiceStatus: billingClaimStatusSettings.selectedInvoiceStatus,
            IsIgnored: billingClaimStatusSettings.isIgnored,
            ProviderSearchText: billingClaimStatusSettings?.providerSearchText ?? ""
        };
    }

    public static fromCacheModel(billingClaimStatusSettingsResponse: BillingClaimStatusFilterSettings): BillingClaimStatusInboxModel {
        if (!billingClaimStatusSettingsResponse)
            return null;

        let providerIds = billingClaimStatusSettingsResponse.SelectedProviderIds;
        if (!sessionStore.hasPermission(EnumMyleLoginPermission.BillingTabPermission)) {
            providerIds = [sessionStore.getCurrentEmployee().ContactFacetId];
        }

        const billingClaimStatusSettingsModel = new BillingClaimStatusInboxModel();
        billingClaimStatusSettingsModel.dateFrom = billingClaimStatusSettingsResponse.DateFrom ?? BillingClaimStatusInboxModel.getPreviousTwoWeeksDate();
        billingClaimStatusSettingsModel.dateTo = billingClaimStatusSettingsResponse.DateTo ?? new Date();
        billingClaimStatusSettingsModel.queryPageSize = billingClaimStatusSettingsResponse.QueryPageSize;
        billingClaimStatusSettingsModel.selectedSiteIds = billingClaimStatusSettingsResponse.SelectedSiteIds ?? [];
        billingClaimStatusSettingsModel.selectedProviderIds = providerIds ?? [];
        billingClaimStatusSettingsModel.selectedInvoiceStatus = billingClaimStatusSettingsResponse.SelectedInvoiceStatus ?? [];
        billingClaimStatusSettingsModel.isIgnored = billingClaimStatusSettingsResponse.IsIgnored;
        billingClaimStatusSettingsModel.providerSearchText = billingClaimStatusSettingsResponse?.ProviderSearchText ?? "";
        billingClaimStatusSettingsModel.followCurrentTime = !billingClaimStatusSettingsResponse.DateFrom;

        return billingClaimStatusSettingsModel;
    }

    public static getDefaultFilterSettings(): BillingClaimStatusInboxModel {
        let selectedProviders: Guid[] = [];
        if (sessionStore.hasPermission(EnumMyleLoginPermission.BillingTabPermission)) {
            selectedProviders = cacheStore.EmployeeCacheWithInactive.map(item => item.ContactFacet_id);
        }
        else {
            selectedProviders = [sessionStore.getCurrentEmployee().ContactFacetId];
        }

        const defaultFilterSettingsModel = new BillingClaimStatusInboxModel();
        defaultFilterSettingsModel.dateFrom = BillingClaimStatusInboxModel.getPreviousTwoWeeksDate();
        defaultFilterSettingsModel.dateTo = new Date();
        defaultFilterSettingsModel.selectedSiteIds = cacheStore.SiteCache.map(site => site.Model.Id);
        defaultFilterSettingsModel.selectedProviderIds = selectedProviders;
        defaultFilterSettingsModel.selectedInvoiceStatus = [BillingClaimInvoiceStatus.NoNote, BillingClaimInvoiceStatus.NoPaymentRequest];
        defaultFilterSettingsModel.isIgnored = false;

        return defaultFilterSettingsModel;
    }

    private static getPreviousTwoWeeksDate(): Date {
        const previousDate = DateTime.now().plus({ weeks: -2 });
        return previousDate.toJSDate();
    }
}

export { BillingClaimStatusInboxModel };
